/**
 * The postMessage contract between the hosted Connect UI and this SDK.
 *
 * ONE parser, shared by the popup/redirect handler and the inline (iframe)
 * handler: the two receive the same messages from different windows, and a
 * contract that lived in each would drift. Every inbound `event.data` is
 * untrusted cross-window input and is parsed in full before any field is
 * consumed, so one malformed nested grant or failure cannot be filtered into
 * an apparent success.
 */

import * as z from "zod/mini";

import type {
  Grant,
  AlterError,
  ConnectCompletion,
  ConnectFailedGrant,
  ConnectSessionKind,
  ManagedSecretOutcome,
} from "../types";

const nonEmptyStringSchema = z.string().check(z.minLength(1));

/** A canonical hyphenated UUID (any case), as the backend sends agent ids. */
export const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const grantMetadataSchema = z.catchall(
  z.object({
    account_display_name: z.optional(z.nullable(z.string())),
    account_email: z.optional(z.string()),
  }),
  z.unknown(),
);

const grantSchema = z.pipe(
  z.object({
    grant_id: nonEmptyStringSchema,
    provider: nonEmptyStringSchema,
    provider_name: z.optional(z.string()),
    account_identifier: z.optional(z.nullable(z.string())),
    timestamp: z.optional(z.string()),
    operation: z._default(
      z.optional(z.enum(["creation", "reauth"])),
      "creation",
    ),
    scopes: z._default(z.optional(z.array(z.string())), []),
    status: z._default(
      z.optional(z.enum(["active", "pending", "error"])),
      "active",
    ),
    metadata: z.optional(grantMetadataSchema),
    // The delegation to the session's agent, when there is one. The known
    // keys are validated strictly (a malformed one is refused rather than
    // reported as a setting the server never stored); an unknown key is
    // stripped, so a page that adds a field never breaks a deployed SDK.
    delegation: z.optional(
      z.object({
        agent_id: z.string().check(z.regex(UUID_PATTERN)),
        delegable: z.boolean(),
        policy_refused: z.optional(z.boolean()),
        // The delegation's own grant id (a managed-secret session's agent).
        grant_id: z.optional(z.string().check(z.regex(UUID_PATTERN))),
      }),
    ),
  }),
  z.transform((value): Grant => {
    const grant: Grant = {
      grant_id: value.grant_id,
      provider: value.provider,
      provider_name: value.provider_name ?? value.provider,
      account_identifier: value.account_identifier ?? null,
      timestamp: value.timestamp ?? new Date().toISOString(),
      operation: value.operation,
      scopes: value.scopes,
      status: value.status,
    };
    if (value.metadata !== undefined) {
      grant.metadata = value.metadata;
    }
    if (value.delegation !== undefined) {
      grant.delegation = {
        agent_id: value.delegation.agent_id,
        delegable: value.delegation.delegable,
        policy_refused: value.delegation.policy_refused ?? false,
      };
      if (value.delegation.grant_id !== undefined) {
        grant.delegation.grant_id = value.delegation.grant_id;
      }
    }
    return grant;
  }),
);

const connectFailedGrantSchema = z.pipe(
  z.object({
    provider_id: nonEmptyStringSchema,
    reason: nonEmptyStringSchema,
    message: nonEmptyStringSchema,
  }),
  z.transform(
    (value): ConnectFailedGrant => ({
      providerId: value.provider_id,
      reason: value.reason,
      message: value.message,
    }),
  ),
);

/**
 * Every message type the hosted UI may send this SDK.
 *
 * - `alter_connect_success` / `alter_connect_error` — terminal.
 * - `alter_connect_event` — a NON-TERMINAL notice (see below).
 * - `alter_connect_exit` — the user pressed Cancel inside the inline widget
 *   (the popup flow has no Cancel; the user closes the window). Terminal,
 *   maps to `exit`.
 * - `alter_connect_embed_unsupported` — the browser refused the inline
 *   widget's cookie and the hosted page offered a window instead. Purely
 *   informational: the hosted page itself opens that window from the user's
 *   click and relays its outcome back through the frame, so the SDK stays
 *   passive and only reports it on the `event` bus.
 * - `alter_connect_ready`: the inline frame's LOAD PROOF. The hosted page
 *   posts it as soon as its script runs inside the frame. A cross-origin
 *   iframe exposes nothing else the SDK can observe: the browser fires `load`
 *   even for a frame it refused (CSP `frame-ancestors`, a network error page,
 *   a JSON 400), so only a message from the page proves it rendered. The
 *   inline handler arms a load timer at mount and this message (or any other
 *   from the frame) disarms it. Never terminal; the popup flow ignores it.
 * - `alter_connect_embed_continue`: the managed-secret page inside the frame
 *   continues the flow in a top-level window it opened (sign-in and key entry
 *   never run in a frame); the outcome comes back through the frame. A
 *   non-terminal notice, reported on the `event` bus as `embed_continue`.
 *
 * Both Connect pages (OAuth and managed-secret) post this one envelope; the
 * managed-secret page's own `alter:managed-secret-connect:*` messages are for
 * integrations that listen without this SDK and are ignored here.
 */
export const CONNECT_MESSAGE_TYPES = [
  "alter_connect_success",
  "alter_connect_error",
  "alter_connect_event",
  "alter_connect_exit",
  "alter_connect_embed_unsupported",
  "alter_connect_embed_continue",
  "alter_connect_ready",
] as const;

const envelopeSchema = z.object({
  type: z.enum(CONNECT_MESSAGE_TYPES),
});

/**
 * What a managed-secret session confirmed (`managed_secret` on its success
 * payload). Strict on the known keys; an unknown key is stripped.
 */
const managedSecretOutcomeSchema = z.pipe(
  z.object({
    path: z.enum(["fast_path", "key_entry", "repair"]),
    credential_kind: z.enum(["developer", "end_user"]),
    preflight: z.nullable(
      z.object({
        status: z.enum(["passed", "inconclusive", "skipped"]),
        message: z.nullable(z.string()),
      }),
    ),
  }),
  z.transform(
    (value): ManagedSecretOutcome => ({
      path: value.path,
      credentialKind: value.credential_kind,
      preflight: value.preflight,
    }),
  ),
);

const successPayloadSchema = z.object({
  type: z.literal("alter_connect_success"),
  grants: z.array(grantSchema),
  failed_grants: z._default(z.optional(z.array(connectFailedGrantSchema)), []),
  managed_secret: z.optional(managedSecretOutcomeSchema),
});

const errorPayloadSchema = z.looseObject({
  type: z.literal("alter_connect_error"),
  error: z.catch(z.string(), "oauth_error"),
  error_description: z.catch(z.string(), "OAuth authorization failed"),
});

/**
 * A NON-TERMINAL notice from the hosted flow.
 *
 * Distinct from `alter_connect_error` on purpose: an error settles the flow and
 * closes the popup, which would be wrong for something the user can still
 * recover from in-place (refusing one provider when the session still allows
 * others). A notice reports what happened and leaves the flow open, so the
 * application is never blind to a real user decision that did not end the flow.
 */
const noticePayloadSchema = z.looseObject({
  type: z.literal("alter_connect_event"),
  event: z.catch(z.string(), "connect_notice"),
  metadata: z.catch(z.record(z.string(), z.unknown()), {}),
});

const embedUnsupportedPayloadSchema = z.looseObject({
  type: z.literal("alter_connect_embed_unsupported"),
  reason: z.optional(z.string()),
});

function malformedSuccessMessage(error: z.core.$ZodError): string {
  const topLevelField = error.issues[0]?.path[0];
  if (topLevelField === "failed_grants") {
    return "Server returned malformed failed_grants data";
  }
  if (topLevelField === "managed_secret") {
    return "Server returned malformed managed-secret completion data";
  }
  if (topLevelField === "grants" && error.issues[0]?.path.length === 1) {
    return "Server returned success without grants array";
  }
  return "Server returned malformed grant data";
}

/**
 * `failed_grants` reasons that are not a usage-limit failure: the user left
 * the provider unticked on the Confirm screen, or the connection could not be
 * turned on when the user confirmed it.
 */
const NON_POLICY_FAILURE_REASONS: ReadonlySet<string> = new Set([
  "not_selected",
  "activation_failed",
]);

/**
 * The error for a success that kept no grant. Any usage-limit reason keeps
 * `grant_policy_application_failed`; only when every failure is
 * `activation_failed` or `not_selected` is it `grant_activation_failed`. An
 * unknown reason, or no failure listed at all, counts as a usage-limit
 * failure, the conservative reading. The server SDKs and the hosted success
 * page's redirect apply the same rule.
 */
export function totalFailureError(
  failedGrants: ConnectFailedGrant[],
): AlterError {
  if (
    failedGrants.length > 0 &&
    failedGrants.every((failure) =>
      NON_POLICY_FAILURE_REASONS.has(failure.reason),
    )
  ) {
    return {
      code: "grant_activation_failed",
      message:
        "The connection could not be completed. Start the connection again.",
      failedGrants,
    };
  }
  return {
    code: "grant_policy_application_failed",
    message:
      "The connection was not completed because the selected usage limits could not be applied.",
    failedGrants,
  };
}

/** What a handler should do with one inbound message. */
export type ConnectMessageOutcome =
  | { kind: "success"; grants: Grant[]; completion: ConnectCompletion }
  | { kind: "error"; error: AlterError }
  | { kind: "notice"; event: string; metadata: Record<string, unknown> }
  | { kind: "exit" }
  | { kind: "ready" }
  | { kind: "ignore" };

/**
 * Classify one inbound `event.data`. Pure: origin and source checks are the
 * caller's job (they differ between a popup and an iframe); this only decides
 * what a message MEANS once it is known to come from the hosted UI.
 *
 * `kind` is the flow's session kind, from the token the widget was given. A
 * managed-secret success must carry `managed_secret` (else `invalid_response`);
 * an OAuth success's `managed_secret`, if any, is ignored. The completion's
 * `kind` is always this argument, never something the message says.
 */
export function parseConnectMessage(
  data: unknown,
  kind: ConnectSessionKind = "oauth",
): ConnectMessageOutcome {
  const envelope = envelopeSchema.safeParse(data);
  if (!envelope.success) {
    return { kind: "ignore" };
  }

  switch (envelope.data.type) {
    case "alter_connect_event": {
      const notice = noticePayloadSchema.safeParse(data);
      if (!notice.success) return { kind: "ignore" };
      return {
        kind: "notice",
        event: notice.data.event,
        metadata: notice.data.metadata,
      };
    }
    case "alter_connect_exit":
      return { kind: "exit" };
    case "alter_connect_ready":
      return { kind: "ready" };
    case "alter_connect_embed_continue":
      return {
        kind: "notice",
        event: "embed_continue",
        metadata: { timestamp: new Date().toISOString() },
      };
    case "alter_connect_embed_unsupported": {
      const parsed = embedUnsupportedPayloadSchema.safeParse(data);
      return {
        kind: "notice",
        event: "embed_unsupported",
        metadata: {
          reason: parsed.success ? (parsed.data.reason ?? null) : null,
          timestamp: new Date().toISOString(),
        },
      };
    }
    case "alter_connect_success": {
      const parsed = successPayloadSchema.safeParse(data);
      if (!parsed.success) {
        return {
          kind: "error",
          error: {
            code: "invalid_response",
            message: malformedSuccessMessage(parsed.error),
          },
        };
      }
      const { grants, failed_grants: failedGrants } = parsed.data;
      const managedSecret = parsed.data.managed_secret;
      if (kind === "managed_secret" && managedSecret === undefined) {
        return {
          kind: "error",
          error: {
            code: "invalid_response",
            message:
              "Server returned a managed-secret completion without its outcome",
          },
        };
      }
      if (grants.length === 0) {
        if (failedGrants.length > 0) {
          return { kind: "error", error: totalFailureError(failedGrants) };
        }
        return {
          kind: "error",
          error: {
            code: "invalid_response",
            message: "Server returned empty grants array",
          },
        };
      }
      const completion: ConnectCompletion =
        kind === "managed_secret" && managedSecret !== undefined
          ? { kind, grants, failedGrants, managedSecret }
          : { kind: "oauth", grants, failedGrants };
      return { kind: "success", grants, completion };
    }
    case "alter_connect_error": {
      const parsed = errorPayloadSchema.safeParse(data);
      if (!parsed.success) return { kind: "ignore" };
      return {
        kind: "error",
        error: {
          code: parsed.data.error,
          message: parsed.data.error_description,
          details: parsed.data,
        },
      };
    }
  }
}
